import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth';
import type {
  CRMContactPermission,
  CRMInteractionChannel,
  CRMInteractionKind,
  CRMInteractionOutcome,
  CRMLead,
  CRMLeadDetail,
  CRMLeadPriority,
  CRMLeadSegment,
  CRMLeadStage,
  CRMMessageDraft,
  CRMReachOutStatus,
  CRMStats,
} from '@modelcast/types';

const SEGMENT_OPTIONS: { value: CRMLeadSegment; label: string }[] = [
  { value: 'model', label: 'Models' },
  { value: 'artist', label: 'Artists' },
  { value: 'educator', label: 'Educators' },
  { value: 'host', label: 'Hosts' },
  { value: 'organization', label: 'Organizations' },
];

const STAGE_OPTIONS: { value: CRMLeadStage; label: string }[] = [
  { value: 'found', label: 'Found' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'personalized', label: 'Personalized' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'replied', label: 'Replied' },
  { value: 'call_booked', label: 'Call booked' },
  { value: 'invited', label: 'Invited' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'activated', label: 'Activated' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'do_not_contact', label: 'Do not contact' },
];

const PRIORITY_OPTIONS: { value: CRMLeadPriority; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const PERMISSION_OPTIONS: { value: CRMContactPermission; label: string }[] = [
  { value: 'public_business', label: 'Public business' },
  { value: 'consented', label: 'Consented' },
  { value: 'existing_relationship', label: 'Existing relationship' },
  { value: 'needs_consent', label: 'Needs consent' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'do_not_contact', label: 'Do not contact' },
];

const REACH_OUT_OPTIONS: { value: CRMReachOutStatus; label: string }[] = [
  { value: 'ready', label: 'Ready' },
  { value: 'needs_review', label: 'Review' },
  { value: 'not_enough_info', label: 'Missing info' },
  { value: 'cooldown', label: 'Cooldown' },
  { value: 'do_not_contact', label: 'Do not contact' },
];

const CHANNEL_OPTIONS: { value: CRMInteractionChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'website_form', label: 'Website form' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' },
];

interface LeadFormState {
  name: string;
  segment: CRMLeadSegment;
  organization: string;
  roleTitle: string;
  email: string;
  socialHandle: string;
  website: string;
  location: string;
  sourceUrl: string;
  sourcePlatform: string;
  fitReason: string;
  priority: CRMLeadPriority;
  contactPermission: CRMContactPermission;
  publicContactMethod: string;
  notes: string;
  tags: string;
}

const emptyLeadForm: LeadFormState = {
  name: '',
  segment: 'model',
  organization: '',
  roleTitle: '',
  email: '',
  socialHandle: '',
  website: '',
  location: '',
  sourceUrl: '',
  sourcePlatform: '',
  fitReason: '',
  priority: 'medium',
  contactPermission: 'unknown',
  publicContactMethod: '',
  notes: '',
  tags: '',
};

function statusClass(status: CRMReachOutStatus): string {
  switch (status) {
    case 'ready':
      return 'bg-ok/15 text-ok border-ok/20';
    case 'needs_review':
      return 'bg-warn/15 text-warn border-warn/20';
    case 'not_enough_info':
      return 'bg-text-faint/15 text-text-dim border-line';
    case 'cooldown':
      return 'bg-panel2 text-text-dim border-line';
    case 'do_not_contact':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
  }
}

function formatDate(value: string | null, withTime = false): string {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, withTime
    ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

function leadToForm(lead: CRMLeadDetail): LeadFormState {
  return {
    name: lead.name,
    segment: lead.segment,
    organization: lead.organization ?? '',
    roleTitle: lead.roleTitle ?? '',
    email: lead.email ?? '',
    socialHandle: lead.socialHandle ?? '',
    website: lead.website ?? '',
    location: lead.location ?? '',
    sourceUrl: lead.sourceUrl ?? '',
    sourcePlatform: lead.sourcePlatform ?? '',
    fitReason: lead.fitReason ?? '',
    priority: lead.priority,
    contactPermission: lead.contactPermission,
    publicContactMethod: lead.publicContactMethod ?? '',
    notes: lead.notes ?? '',
    tags: lead.tags.join(', '),
  };
}

function serializeLeadForm(form: LeadFormState) {
  return {
    ...form,
    tags: form.tags.split(',').map(tag => tag.trim()).filter(Boolean),
  };
}

export function CRMPage() {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<CRMStats | null>(null);
  const [leads, setLeads] = useState<CRMLead[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedLead, setSelectedLead] = useState<CRMLeadDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadFormState>(emptyLeadForm);
  const [editForm, setEditForm] = useState<LeadFormState | null>(null);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<CRMLeadSegment | ''>('');
  const [stageFilter, setStageFilter] = useState<CRMLeadStage | ''>('');
  const [reachOutFilter, setReachOutFilter] = useState<CRMReachOutStatus | ''>('ready');
  const [page, setPage] = useState(0);
  const [draftChannel, setDraftChannel] = useState<CRMInteractionChannel>('email');
  const [draftGoal, setDraftGoal] = useState('');
  const [draft, setDraft] = useState<CRMMessageDraft | null>(null);
  const [interactionKind, setInteractionKind] = useState<CRMInteractionKind>('note');
  const [interactionChannel, setInteractionChannel] = useState<CRMInteractionChannel>('email');
  const [interactionOutcome, setInteractionOutcome] = useState<CRMInteractionOutcome | ''>('');
  const [interactionBody, setInteractionBody] = useState('');
  const limit = 25;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [search, segmentFilter, stageFilter, reachOutFilter, page]);

  async function apiFetch(path: string, init: RequestInit = {}) {
    const token = await getToken();
    return fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...init.headers,
      },
    });
  }

  async function fetchStats() {
    try {
      const response = await apiFetch('/api/admin/crm/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching CRM stats:', error);
    }
  }

  async function fetchLeads() {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (segmentFilter) params.set('segment', segmentFilter);
      if (stageFilter) params.set('stage', stageFilter);
      if (reachOutFilter) params.set('reachOutStatus', reachOutFilter);
      params.set('limit', String(limit));
      params.set('offset', String(page * limit));

      const response = await apiFetch(`/api/admin/crm/leads?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
        setTotal(data.total);
        if (!selectedLead && data.leads[0]) {
          fetchLead(data.leads[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching CRM leads:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchLead(id: string) {
    try {
      const response = await apiFetch(`/api/admin/crm/leads/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedLead(data.lead);
        setEditForm(leadToForm(data.lead));
        setDraft(null);
        setDraftChannel(data.lead.recommendedContactRoute?.channel ?? (data.lead.email ? 'email' : 'instagram'));
      }
    } catch (error) {
      console.error('Error fetching CRM lead:', error);
    }
  }

  async function handleCreateLead(event: React.FormEvent) {
    event.preventDefault();
    setIsSaving(true);
    try {
      const response = await apiFetch('/api/admin/crm/leads', {
        method: 'POST',
        body: JSON.stringify(serializeLeadForm(leadForm)),
      });
      if (response.ok) {
        const data = await response.json();
        setLeadForm(emptyLeadForm);
        setShowNewLead(false);
        await fetchStats();
        await fetchLeads();
        await fetchLead(data.lead.id);
      }
    } catch (error) {
      console.error('Error creating CRM lead:', error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveLead(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedLead || !editForm) return;

    setIsSaving(true);
    try {
      const response = await apiFetch(`/api/admin/crm/leads/${selectedLead.id}`, {
        method: 'PATCH',
        body: JSON.stringify(serializeLeadForm(editForm)),
      });
      if (response.ok) {
        await fetchStats();
        await fetchLeads();
        await fetchLead(selectedLead.id);
      }
    } catch (error) {
      console.error('Error saving CRM lead:', error);
    } finally {
      setIsSaving(false);
    }
  }

  async function quickUpdateLead(updates: Partial<CRMLead>) {
    if (!selectedLead) return;

    try {
      const response = await apiFetch(`/api/admin/crm/leads/${selectedLead.id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        await fetchStats();
        await fetchLeads();
        await fetchLead(selectedLead.id);
      }
    } catch (error) {
      console.error('Error updating CRM lead:', error);
    }
  }

  async function handleDraftMessage() {
    if (!selectedLead) return;

    try {
      const response = await apiFetch(`/api/admin/crm/leads/${selectedLead.id}/draft-message`, {
        method: 'POST',
        body: JSON.stringify({ channel: draftChannel, goal: draftGoal }),
      });
      if (response.ok) {
        const data = await response.json();
        setDraft(data.draft);
        await fetchLead(selectedLead.id);
      }
    } catch (error) {
      console.error('Error drafting CRM message:', error);
    }
  }

  async function handleLogSent() {
    if (!selectedLead || !draft) return;

    try {
      const response = await apiFetch(`/api/admin/crm/leads/${selectedLead.id}/interactions`, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'outreach',
          channel: draft.channel,
          direction: 'outbound',
          subject: draft.subject,
          body: draft.body,
          outcome: 'sent',
        }),
      });
      if (response.ok) {
        setDraft(null);
        await fetchStats();
        await fetchLeads();
        await fetchLead(selectedLead.id);
      }
    } catch (error) {
      console.error('Error logging CRM outreach:', error);
    }
  }

  async function handleAddInteraction(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedLead || !interactionBody.trim()) return;

    try {
      const response = await apiFetch(`/api/admin/crm/leads/${selectedLead.id}/interactions`, {
        method: 'POST',
        body: JSON.stringify({
          kind: interactionKind,
          channel: interactionChannel,
          direction: interactionKind === 'reply' ? 'inbound' : 'internal',
          body: interactionBody,
          outcome: interactionOutcome || null,
        }),
      });
      if (response.ok) {
        setInteractionBody('');
        setInteractionOutcome('');
        await fetchStats();
        await fetchLeads();
        await fetchLead(selectedLead.id);
      }
    } catch (error) {
      console.error('Error adding CRM interaction:', error);
    }
  }

  async function handleDoNotContact() {
    if (!selectedLead) return;
    await quickUpdateLead({
      stage: 'do_not_contact',
      contactPermission: 'do_not_contact',
      doNotContact: true,
      optedOutAt: new Date().toISOString(),
    } as Partial<CRMLead>);
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Outreach CRM</h1>
          <p className="text-sm text-text-dim mt-1">
            Manage models, artists, educators, and hosts through a respectful launch pipeline.
          </p>
        </div>
        <button
          onClick={() => setShowNewLead(value => !value)}
          className="btn btn-accent btn-sm"
        >
          {showNewLead ? 'Close' : 'New lead'}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
          <StatBox label="Leads" value={stats.totalLeads} />
          <StatBox label="Ready" value={stats.readyToContact} />
          <StatBox label="Review" value={stats.needsReview} />
          <StatBox label="This week" value={stats.contactedThisWeek} />
          <StatBox label="Replies" value={stats.replies} />
          <StatBox label="Suppressed" value={stats.doNotContact} />
        </div>
      )}

      {showNewLead && (
        <form onSubmit={handleCreateLead} className="bg-panel border border-line rounded-lg p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input label="Name" value={leadForm.name} onChange={value => setLeadForm(form => ({ ...form, name: value }))} required />
            <Select label="Segment" value={leadForm.segment} options={SEGMENT_OPTIONS} onChange={value => setLeadForm(form => ({ ...form, segment: value as CRMLeadSegment }))} />
            <Select label="Priority" value={leadForm.priority} options={PRIORITY_OPTIONS} onChange={value => setLeadForm(form => ({ ...form, priority: value as CRMLeadPriority }))} />
            <Select label="Permission" value={leadForm.contactPermission} options={PERMISSION_OPTIONS} onChange={value => setLeadForm(form => ({ ...form, contactPermission: value as CRMContactPermission }))} />
            <Input label="Email" value={leadForm.email} onChange={value => setLeadForm(form => ({ ...form, email: value }))} />
            <Input label="Social" value={leadForm.socialHandle} onChange={value => setLeadForm(form => ({ ...form, socialHandle: value }))} />
            <Input label="Website" value={leadForm.website} onChange={value => setLeadForm(form => ({ ...form, website: value }))} />
            <Input label="Source" value={leadForm.sourcePlatform} onChange={value => setLeadForm(form => ({ ...form, sourcePlatform: value }))} />
            <Input label="Organization" value={leadForm.organization} onChange={value => setLeadForm(form => ({ ...form, organization: value }))} />
            <Input label="Role" value={leadForm.roleTitle} onChange={value => setLeadForm(form => ({ ...form, roleTitle: value }))} />
            <Input label="Location" value={leadForm.location} onChange={value => setLeadForm(form => ({ ...form, location: value }))} />
            <Input label="Source URL" value={leadForm.sourceUrl} onChange={value => setLeadForm(form => ({ ...form, sourceUrl: value }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <Textarea label="Why they fit" value={leadForm.fitReason} onChange={value => setLeadForm(form => ({ ...form, fitReason: value }))} />
            <Textarea label="Notes" value={leadForm.notes} onChange={value => setLeadForm(form => ({ ...form, notes: value }))} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn btn-sm" onClick={() => setLeadForm(emptyLeadForm)}>
              Clear
            </button>
            <button type="submit" className="btn btn-accent btn-sm" disabled={isSaving || !leadForm.name.trim()}>
              Save lead
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search leads..."
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          className="input h-10 rounded-lg text-sm flex-1 min-w-[220px] max-w-md"
        />
        <select
          value={segmentFilter}
          onChange={(event) => { setSegmentFilter(event.target.value as CRMLeadSegment | ''); setPage(0); }}
          className="input h-10 rounded-lg text-sm w-auto"
        >
          <option value="">All segments</option>
          {SEGMENT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select
          value={stageFilter}
          onChange={(event) => { setStageFilter(event.target.value as CRMLeadStage | ''); setPage(0); }}
          className="input h-10 rounded-lg text-sm w-auto"
        >
          <option value="">All stages</option>
          {STAGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select
          value={reachOutFilter}
          onChange={(event) => { setReachOutFilter(event.target.value as CRMReachOutStatus | ''); setPage(0); }}
          className="input h-10 rounded-lg text-sm w-auto"
        >
          <option value="">All readiness</option>
          {REACH_OUT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(420px,0.9fr)] gap-5">
        <div className="bg-panel border border-line rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-panel2 border-b border-line">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Lead</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Segment</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Stage</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Readiness</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Next</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-text-dim">Loading...</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-text-dim">No leads in this view</td>
                </tr>
              ) : leads.map(lead => (
                <tr
                  key={lead.id}
                  onClick={() => fetchLead(lead.id)}
                  className={`border-b border-line last:border-0 cursor-pointer transition-colors ${
                    selectedLead?.id === lead.id ? 'bg-panel2' : 'hover:bg-panel2/60'
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-text">{lead.name}</div>
                    <div className="text-xs text-text-dim truncate max-w-[260px]">
                      {lead.organization || lead.email || lead.socialHandle || lead.website || 'No contact route yet'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-dim capitalize">{lead.segment}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-[11px] rounded border border-line bg-bg text-text-dim capitalize">
                      {formatLabel(lead.stage)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-[11px] rounded border ${statusClass(lead.reachOut.status)}`}>
                        {lead.reachOut.label}
                      </span>
                      <span className="text-xs text-text-faint">{lead.reachOut.score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-dim">
                    {lead.nextActionAt ? formatDate(lead.nextActionAt) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-line">
              <div className="text-sm text-text-dim">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPage(value => value - 1)} disabled={page === 0} className="btn btn-sm disabled:opacity-50">
                  Previous
                </button>
                <button onClick={() => setPage(value => value + 1)} disabled={page >= totalPages - 1} className="btn btn-sm disabled:opacity-50">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedLead && editForm ? (
          <div className="space-y-5">
            <section className="bg-panel border border-line rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-display font-medium text-text">{selectedLead.name}</h2>
                    <span className={`px-2 py-1 text-[11px] rounded border ${statusClass(selectedLead.reachOut.status)}`}>
                      {selectedLead.reachOut.label}
                    </span>
                  </div>
                  <p className="text-sm text-text-dim">{selectedLead.reachOut.nextBestAction}</p>
                </div>
                <button onClick={handleDoNotContact} className="btn btn-sm text-red-600 border-red-500/30 hover:bg-red-500/10">
                  Suppress
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <Info label="Email" value={selectedLead.email} />
                <Info label="Social" value={selectedLead.socialHandle} />
                <Info label="Website" value={selectedLead.website} link />
                <Info label="Source" value={selectedLead.sourceUrl || selectedLead.sourcePlatform} link={Boolean(selectedLead.sourceUrl)} />
              </div>

              <ContactRouteInfo lead={selectedLead} />

              {(selectedLead.reachOut.reasons.length > 0 || selectedLead.reachOut.blockers.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <ReasonList title="Why reach out" items={selectedLead.reachOut.reasons} />
                  <ReasonList title="Watch-outs" items={selectedLead.reachOut.blockers} />
                </div>
              )}
            </section>

            <form onSubmit={handleSaveLead} className="bg-panel border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text">Lead details</h3>
                <button type="submit" className="btn btn-sm" disabled={isSaving}>
                  Save changes
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Name" value={editForm.name} onChange={value => setEditForm(form => form && ({ ...form, name: value }))} required />
                <Select label="Segment" value={editForm.segment} options={SEGMENT_OPTIONS} onChange={value => setEditForm(form => form && ({ ...form, segment: value as CRMLeadSegment }))} />
                <Select label="Stage" value={selectedLead.stage} options={STAGE_OPTIONS} onChange={value => quickUpdateLead({ stage: value as CRMLeadStage })} />
                <Select label="Priority" value={editForm.priority} options={PRIORITY_OPTIONS} onChange={value => setEditForm(form => form && ({ ...form, priority: value as CRMLeadPriority }))} />
                <Select label="Permission" value={editForm.contactPermission} options={PERMISSION_OPTIONS} onChange={value => setEditForm(form => form && ({ ...form, contactPermission: value as CRMContactPermission }))} />
                <Input label="Public contact" value={editForm.publicContactMethod} onChange={value => setEditForm(form => form && ({ ...form, publicContactMethod: value }))} />
                <Input label="Email" value={editForm.email} onChange={value => setEditForm(form => form && ({ ...form, email: value }))} />
                <Input label="Social" value={editForm.socialHandle} onChange={value => setEditForm(form => form && ({ ...form, socialHandle: value }))} />
                <Input label="Website" value={editForm.website} onChange={value => setEditForm(form => form && ({ ...form, website: value }))} />
                <Input label="Source URL" value={editForm.sourceUrl} onChange={value => setEditForm(form => form && ({ ...form, sourceUrl: value }))} />
                <Input label="Organization" value={editForm.organization} onChange={value => setEditForm(form => form && ({ ...form, organization: value }))} />
                <Input label="Role" value={editForm.roleTitle} onChange={value => setEditForm(form => form && ({ ...form, roleTitle: value }))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Textarea label="Fit reason" value={editForm.fitReason} onChange={value => setEditForm(form => form && ({ ...form, fitReason: value }))} />
                <Textarea label="Notes" value={editForm.notes} onChange={value => setEditForm(form => form && ({ ...form, notes: value }))} />
              </div>
            </form>

            <section className="bg-panel border border-line rounded-lg p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-semibold text-text">Draft outreach</h3>
                <div className="flex items-center gap-2">
                  <select
                    value={draftChannel}
                    onChange={event => setDraftChannel(event.target.value as CRMInteractionChannel)}
                    className="input h-9 rounded-lg text-sm w-auto"
                  >
                    {CHANNEL_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <button onClick={handleDraftMessage} className="btn btn-sm" type="button">
                    Draft
                  </button>
                </div>
              </div>
              <input
                value={draftGoal}
                onChange={event => setDraftGoal(event.target.value)}
                placeholder="Optional ask, e.g. Would you be open to a 15-minute call?"
                className="input h-10 rounded-lg text-sm mb-3"
              />
              {draft ? (
                <div className="space-y-3">
                  <input
                    value={draft.subject}
                    onChange={event => setDraft(current => current && ({ ...current, subject: event.target.value }))}
                    className="input h-10 rounded-lg text-sm"
                  />
                  <textarea
                    value={draft.body}
                    onChange={event => setDraft(current => current && ({ ...current, body: event.target.value }))}
                    className="input min-h-[190px] h-auto rounded-lg text-sm py-3 leading-relaxed"
                  />
                  <div className="bg-bg border border-line rounded-lg p-3">
                    <div className="text-[10px] font-mono uppercaser text-text-faint mb-2">Compliance checks</div>
                    <ul className="space-y-1">
                      {draft.complianceNotes.map(note => (
                        <li key={note} className="text-xs text-text-dim">{note}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setDraft(null)} className="btn btn-sm">
                      Discard
                    </button>
                    <button type="button" onClick={handleLogSent} className="btn btn-accent btn-sm">
                      Mark sent
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-line rounded-lg p-5 text-sm text-text-dim">
                  Select a channel and draft a short message from the current lead context.
                </div>
              )}
            </section>

            <section className="bg-panel border border-line rounded-lg p-4">
              <h3 className="text-sm font-semibold text-text mb-3">Replies and notes</h3>
              <form onSubmit={handleAddInteraction} className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <select value={interactionKind} onChange={event => setInteractionKind(event.target.value as CRMInteractionKind)} className="input h-10 rounded-lg text-sm">
                    <option value="note">Note</option>
                    <option value="reply">Reply</option>
                    <option value="call">Call</option>
                    <option value="status_change">Status</option>
                  </select>
                  <select value={interactionChannel} onChange={event => setInteractionChannel(event.target.value as CRMInteractionChannel)} className="input h-10 rounded-lg text-sm">
                    {CHANNEL_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <select value={interactionOutcome} onChange={event => setInteractionOutcome(event.target.value as CRMInteractionOutcome | '')} className="input h-10 rounded-lg text-sm">
                    <option value="">No outcome</option>
                    <option value="positive_reply">Positive reply</option>
                    <option value="neutral_reply">Neutral reply</option>
                    <option value="negative_reply">Negative reply</option>
                    <option value="call_booked">Call booked</option>
                    <option value="invited">Invited</option>
                    <option value="onboarded">Onboarded</option>
                    <option value="opted_out">Opted out</option>
                  </select>
                </div>
                <textarea
                  value={interactionBody}
                  onChange={event => setInteractionBody(event.target.value)}
                  placeholder="Paste a reply, call note, or internal note..."
                  className="input min-h-[96px] h-auto rounded-lg text-sm py-3"
                />
                <div className="flex justify-end">
                  <button type="submit" className="btn btn-sm" disabled={!interactionBody.trim()}>
                    Log
                  </button>
                </div>
              </form>

              <div className="mt-5 space-y-3">
                {selectedLead.interactions.length === 0 ? (
                  <div className="text-sm text-text-dim">No activity yet</div>
                ) : selectedLead.interactions.map(interaction => (
                  <div key={interaction.id} className="border border-line rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-xs text-text-dim">
                        <span className="font-medium text-text capitalize">{formatLabel(interaction.kind)}</span>
                        <span> via {formatLabel(interaction.channel)}</span>
                        {interaction.outcome && <span> · {formatLabel(interaction.outcome)}</span>}
                      </div>
                      <div className="text-xs text-text-faint">{formatDate(interaction.createdAt, true)}</div>
                    </div>
                    {interaction.subject && (
                      <div className="text-sm font-medium text-text mt-2">{interaction.subject}</div>
                    )}
                    <div className="text-sm text-text-dim whitespace-pre-wrap mt-2">{interaction.body}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="bg-panel border border-line rounded-lg p-8 text-center text-text-dim">
            Select a lead to work the pipeline.
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-panel border border-line rounded-lg p-3">
      <div className="text-[10px] font-mono text-text-faint uppercaser">{label}</div>
      <div className="text-2xl font-display text-text mt-1">{value}</div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] text-text-dim mb-1">{label}</span>
      <input
        value={value}
        onChange={event => onChange(event.target.value)}
        required={required}
        className="input h-10 rounded-lg text-sm"
      />
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] text-text-dim mb-1">{label}</span>
      <textarea
        value={value}
        onChange={event => onChange(event.target.value)}
        className="input min-h-[88px] h-auto rounded-lg text-sm py-3"
      />
    </label>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] text-text-dim mb-1">{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="input h-10 rounded-lg text-sm"
      >
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function Info({ label, value, link = false }: { label: string; value: string | null; link?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercaser text-text-faint">{label}</div>
      {value ? (
        link ? (
          <a href={value} target="_blank" rel="noreferrer" className="text-sm text-text hover:underline break-all">
            {value}
          </a>
        ) : (
          <div className="text-sm text-text break-all">{value}</div>
        )
      ) : (
        <div className="text-sm text-text-faint">-</div>
      )}
    </div>
  );
}

function ContactRouteInfo({ lead }: { lead: CRMLeadDetail }) {
  const route = lead.recommendedContactRoute;

  return (
    <div className="mt-4 bg-bg border border-line rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercaser text-text-faint">Recommended route</div>
          <div className="text-sm font-medium text-text mt-1">
            {route.label}
            <span className="ml-2 text-xs text-text-faint">{route.confidence}</span>
          </div>
          <p className="text-xs text-text-dim mt-1">{route.reason}</p>
        </div>
        {route.available && route.href && (
          <a href={route.href} target="_blank" rel="noreferrer" className="btn btn-sm shrink-0">
            {route.actionLabel}
          </a>
        )}
      </div>
    </div>
  );
}

function ReasonList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="bg-bg border border-line rounded-lg p-3">
      <div className="text-[10px] font-mono uppercaser text-text-faint mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-text-dim">Clear</div>
      ) : (
        <ul className="space-y-1">
          {items.map(item => <li key={item} className="text-xs text-text-dim">{item}</li>)}
        </ul>
      )}
    </div>
  );
}
